import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Spinner,
    InputGroup,
} from 'react-bootstrap';
import axiosInstance from '../axiosInstance';
import logo from '../assets/svg/gptw_logo_emp.svg';
import LanguageSelector from '../components/LanguageSelector';
import eyeIcon from '../assets/gptw_icons/Icons_weiß/eye.png';
import eyeOffIcon from '../assets/gptw_icons/Icons_weiß/eye-off.png';

const ResetPassword = () => {
    const { uid: paramUid, token: paramToken } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Get uid and token from either URL params or query params
    const uid = paramUid || searchParams.get('uid');
    const token = paramToken || searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [tokenLoading, setTokenLoading] = useState(true); // Loading state for token validation
    const [resetLoading, setResetLoading] = useState(false); // Loading state for resetting password
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        document.title = 'TRAC - Passwort zurücksetzen';
      }, []);

    useEffect(() => {
        const validateToken = async () => {
            if (!uid || !token) {
                navigate('/login', { replace: true });
                return;
            }

            try {
                await axiosInstance.get(`/api/validate-reset-token/?uid=${uid}&token=${token}`);
                setTokenLoading(false);
            } catch (err) {
                navigate('/login', { replace: true });
            }
        };

        validateToken();
    }, [uid, token, navigate]);

    if (tokenLoading) {
        return (
            <Container fluid className="vh-100 d-flex align-items-center justify-content-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Laden...</span>
                </Spinner>
            </Container>
        );
    }

    // Check each password criteria
    const getPasswordCriteria = (password) => ({
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        digit: /[0-9]/.test(password),
        specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });

    const criteria = getPasswordCriteria(newPassword);

    // All criteria must be met
    const validatePassword = (password) =>
        Object.values(getPasswordCriteria(password)).every(Boolean);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);

        if (newPassword !== confirmPassword) {
            setError('Die Passwörter stimmen nicht überein.');
            return;
        }
        if (!validatePassword(newPassword)) {
            setError(
                'Das Passwort entspricht nicht den Sicherheitsanforderungen. Bitte überprüfe die Kriterien.'
            );
            return;
        }

        setResetLoading(true);
        try {
            await axiosInstance.post('/api/reset-password-confirm/', {
                uid: uid,
                token: token,
                new_password: newPassword,
            });
            setSuccessMessage(
                'Dein Passwort wurde erfolgreich zurückgesetzt. Du wirst in Kürze zur Anmeldeseite weitergeleitet.'
            );
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.'
            );
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <Container fluid className="vh-100 d-flex flex-column overflow-hidden">
            <Row className="flex-grow-1 g-0">
                <Col
                    lg={4}
                    className="d-flex flex-column align-items-center justify-content-center overflow-auto"
                >
                    <Row className="w-100 mb-5 mt-3">
                        <Col xs={12} className="d-flex justify-content-end">
                            <LanguageSelector />
                        </Col>
                        <Col xs={12} className="d-flex justify-content-center mb-3">
                            <div className="d-flex justify-content-center w-100">
                                <img
                                    src={logo}
                                    alt="GPTW company logo"
                                    className="img-fluid"
                                    width="130"
                                    height="130"
                                />
                            </div>
                        </Col>
                    </Row>
                    <div className="login-form text-center flex-grow-1 w-100 px-4">
                        <h2 className="mb-5">Passwort zurücksetzen</h2>
                        <Form onSubmit={handleSubmit}>
                            <Form.Group controlId="newPassword" className="mb-4">
                                <Form.Label>Neues Passwort</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Neues Passwort eingeben"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        style={{ padding: '0.375rem 0.75rem' }}
                                    >
                                        <img
                                            src={showPassword ? eyeOffIcon : eyeIcon}
                                            alt={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </Button>
                                </InputGroup>
                                {newPassword && (
                                    <>
                                        <ul className="list-unstyled text-start mt-2">
                                            <li className={criteria.length ? 'text-success' : 'text-danger'}>
                                                {criteria.length ? '✓' : '✗'} Mindestens 8 Zeichen
                                            </li>
                                            <li className={criteria.uppercase ? 'text-success' : 'text-danger'}>
                                                {criteria.uppercase ? '✓' : '✗'} Mindestens einen Großbuchstaben
                                            </li>
                                            <li className={criteria.lowercase ? 'text-success' : 'text-danger'}>
                                                {criteria.lowercase ? '✓' : '✗'} Mindestens einen Kleinbuchstaben
                                            </li>
                                            <li className={criteria.digit ? 'text-success' : 'text-danger'}>
                                                {criteria.digit ? '✓' : '✗'} Mindestens eine Zahl
                                            </li>
                                            <li className={criteria.specialChar ? 'text-success' : 'text-danger'}>
                                                {criteria.specialChar ? '✓' : '✗'} Mindestens ein Sonderzeichen
                                            </li>
                                        </ul>
                                    </>
                                )}
                            </Form.Group>
                            <Form.Group controlId="confirmPassword" className="mb-4">
                                <Form.Label>Passwort bestätigen</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Passwort bestätigen"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        style={{ padding: '0.375rem 0.75rem' }}
                                    >
                                        <img
                                            src={showPassword ? eyeOffIcon : eyeIcon}
                                            alt={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </Button>
                                </InputGroup>
                            </Form.Group>
                            <Button
                                type="submit"
                                className="d-flex align-items-center justify-content-center mx-auto mb-3"
                                disabled={resetLoading}
                                variant="primary"
                            >
                                {resetLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Zurücksetzen...
                                    </>
                                ) : (
                                    'Passwort zurücksetzen'
                                )}
                            </Button>
                            {error && (
                                <Alert variant="danger" className="mt-3">
                                    {error}
                                </Alert>
                            )}
                            {successMessage && (
                                <Alert variant="success" className="mt-3">
                                    {successMessage}
                                </Alert>
                            )}
                        </Form>
                    </div>
                    <footer className="login-footer text-center w-100 mt-auto pb-3">
                        <p className="mb-0">
                            © Great Place to Work ® Institute. Alle Rechte vorbehalten.
                        </p>
                    </footer>
                </Col>
                <Col
                    lg={8}
                    className="bg-gray-2 d-flex align-items-center justify-content-center"
                >
                    <img
                        src={logo}
                        alt="GPTW company logo"
                        className="img-fluid"
                        width="480"
                        height="480"
                    />
                </Col>
            </Row>
        </Container>
    );
};

export default ResetPassword;